import {useState} from "react"
import {useMount} from "react-use"
import {head, map} from "lodash"
import {Switch} from "antd"
import {KlassDropdown} from "components/Select"
import {DigitalTaskCondition, DigitalDocumentFieldSetUp} from "types/tasks"
import {useDigitalSetup} from "../../useDigitalSetup"
import styles from "./FieldActionItem.module.css"

type Props = {
    field: any
    fields: any[]
    isEditable: boolean
    onChangeField: (newField: any) => void
}

export function ConditionalRules(props: Props) {
    const {field, fields, isEditable, onChangeField} = props
    const [selectFieldValues, setSelectFieldValues] = useState([])
    const {conditionOptions, multiFieldValuesOptions, labelByType, fieldHasMultipleOptions} = useDigitalSetup()
    const fieldOptions = fields
        .filter(
            (item) =>
                item.id !== field.id &&
                !item.parentId &&
                !!item.uuid &&
                [
                    DigitalDocumentFieldSetUp.SELECT,
                    DigitalDocumentFieldSetUp.TEXT,
                    DigitalDocumentFieldSetUp.SIGNATURE,
                    DigitalDocumentFieldSetUp.CHECKBOX,
                    DigitalDocumentFieldSetUp.INITIAL,
                    DigitalDocumentFieldSetUp.FILE
                ].includes(item.type)
        )
        .map((item) => {
            const description = map(item.signatureTypes, (signer) => signer.label).join(", ")
            return {
                uuid: item.uuid,
                type: item.type,
                name: item.label
                    ? `${item.label} (${description})`
                    : `${labelByType[item.type]} ${item.fieldIndex} (${description})`
            }
        })

    useMount(() => {
        const dependencyField = field.dependencies?.find(
            (dependencyField: any) => dependencyField.condition?.id === DigitalTaskCondition.EQUALS_TO
        )
        if (dependencyField) {
            const dependencyFieldData = fields.find((item) => item.uuid === dependencyField.uuid.uuid)
            if (fieldHasMultipleOptions.includes(dependencyFieldData?.type)) {
                setSelectFieldValues(dependencyFieldData.fieldValues)
            }
        }
    })

    const onChangeConditionalRule = (value) => {
        if (!isEditable) {
            return
        }
        field.isConditionalRule = value
        field.dependencies = value ? [{uuid: null, value: null, condition: null}] : []
        onChangeField(field)
    }

    const onChangeConditionRule = (idx: number, key: "uuid" | "condition" | "value", value) => {
        if (!isEditable) {
            return
        }
        field.dependencies = field.dependencies.map((dependencyField, itemIdx) => {
            if (itemIdx === idx) {
                if (key === "uuid") {
                    const dependencyFieldData = fields.find((item) => item.uuid === value?.uuid)
                    const defaultConditionValue = fieldHasMultipleOptions.includes(dependencyFieldData?.type)
                        ? head(multiFieldValuesOptions)
                        : head(conditionOptions)
                    let defaultValue = null
                    if (fieldHasMultipleOptions.includes(dependencyFieldData?.type)) {
                        setSelectFieldValues(dependencyFieldData.fieldValues)
                        defaultValue = head(dependencyFieldData.fieldValues)
                    }
                    return {...dependencyField, uuid: value, condition: defaultConditionValue, value: defaultValue}
                } else if (key === "condition") {
                    let defaultValue = null
                    const dependencyFieldData = fields.find((item) => item.uuid === dependencyField.uuid?.uuid)
                    if (fieldHasMultipleOptions.includes(dependencyFieldData?.type)) {
                        setSelectFieldValues(dependencyFieldData.fieldValues)
                        defaultValue = head(dependencyFieldData.fieldValues)
                    }
                    return {...dependencyField, condition: value, value: defaultValue}
                }
                return {...dependencyField, [key]: value}
            }
            return dependencyField
        })
        onChangeField(field)
    }

    const renderConditionContent = (dependencyField, idx) => {
        if (!dependencyField.uuid) {
            return null
        }
        const dependencyFieldData = fields.find((item) => item.uuid === dependencyField.uuid?.uuid)
        if (fieldHasMultipleOptions.includes(dependencyFieldData?.type)) {
            return (
                <>
                    <KlassDropdown
                        options={multiFieldValuesOptions}
                        value={dependencyField.condition}
                        onChange={(value) => onChangeConditionRule(idx, "condition", value)}
                    />
                    {dependencyField.condition.id === DigitalTaskCondition.EQUALS_TO && (
                        <KlassDropdown
                            options={selectFieldValues}
                            value={dependencyField.value}
                            getOptionLabel={(option) => option.label || option.option || `Option ${option.index}`}
                            onChange={(value) => onChangeConditionRule(idx, "value", value)}
                        />
                    )}
                </>
            )
        }
        return (
            <KlassDropdown
                options={conditionOptions}
                value={dependencyField.condition}
                onChange={(value) => onChangeConditionRule(idx, "condition", value)}
            />
        )
    }

    const renderContent = () => {
        if (!field.isConditionalRule) {
            return null
        }

        return (
            <div className={styles.conditionalRulesContent}>
                <span>Show this field when:</span>
                {field.dependencies.map((dependencyField, idx) => (
                    <div key={idx} className={styles.conditionalRulesContent}>
                        <KlassDropdown
                            options={fieldOptions}
                            valueKey="uuid"
                            value={fieldOptions.find((item) => item.uuid === dependencyField.uuid?.uuid)}
                            onChange={(value) => onChangeConditionRule(idx, "uuid", value)}
                        />
                        {renderConditionContent(dependencyField, idx)}
                    </div>
                ))}
            </div>
        )
    }

    return (
        <div className={styles.conditionalRules}>
            <div className={styles.addConditionHeader}>
                <span>Add Conditional Rule</span>
                <Switch checked={field.isConditionalRule} onChange={(checked) => onChangeConditionalRule(checked)} />
            </div>
            {renderContent()}
        </div>
    )
}
